import store from '../../../../state';
import { formatRain, formatSnow } from '../../../../utils/unitConversion';
import { ObservedBinaryService } from '../../../../services/ObservedBinaryService';
import TierAccessService from '../../../../services/TierAccessService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class RecentWeatherWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Recent Rainfall';
    const data = ctx.data;

    const obs = data.recentObserved;
    if (!obs && (ObservedBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('observed_point_tiles'))) {
      return `
        <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${title}</span>
            <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(14,165,233,0.15); color: #38bdf8; border-radius: 4px; font-weight: 600;">NOAA MRMS / SNODAS</span>
          </div>
          ${TierAccessService.buildTrendChartDenialHtml({
            denial: ObservedBinaryService.lastAccessDenial,
            requiredTier: 'pro',
            featureLabel: 'NOAA MRMS & SNODAS recent precipitation telemetry',
            compact: true
          })}
        </div>
      `;
    }
    const isImperial = store.getState().units === 'imperial';
    const units = isImperial ? 'imperial' : 'metric';

    // Real observed rainfall totals (in inches)
    const r1h = obs ? Number(obs.rain1hInches.toFixed(2)) : 0.00;
    const r24h = obs ? Number(obs.rain24hInches.toFixed(2)) : 0.00;
    const r72h = obs ? Number(obs.rain72hInches.toFixed(2)) : 0.00;

    // Real observed snowfall totals & base depth (in inches)
    const s24h = obs ? Number(obs.snow24hInches.toFixed(1)) : 0.0;
    const s72h = obs ? Number(obs.snow72hInches.toFixed(1)) : 0.0;
    const sBase = obs ? Math.round(obs.snowBaseDepthInches) : 0;

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${title}</span>
          <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(14,165,233,0.15); color: #38bdf8; border-radius: 4px; font-weight: 600;">NOAA MRMS / SNODAS</span>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <div style="background: rgba(15,23,42,0.6); padding: 8px 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.06);">
            <div style="font-size: 0.68rem; font-weight: 700; color: #38bdf8; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
              <span>🌧️</span> RAINFALL TOTALS
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 0.75rem; margin-bottom: 3px; color: #94a3b8;">
              <span>Past 1h:</span><span style="font-weight: 700; color: #f8fafc; font-family: monospace;">${formatRain(r1h, units, 1)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 0.75rem; margin-bottom: 3px; color: #94a3b8;">
              <span>Past 24h:</span><span style="font-weight: 700; color: #38bdf8; font-family: monospace;">${formatRain(r24h, units, 1)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 0.75rem; color: #94a3b8;">
              <span>Past 72h:</span><span style="font-weight: 700; color: #f8fafc; font-family: monospace;">${formatRain(r72h, units, 1)}</span>
            </div>
          </div>
          <div style="background: rgba(15,23,42,0.6); padding: 8px 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.06);">
            <div style="font-size: 0.68rem; font-weight: 700; color: #e2e8f0; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
              <span>❄️</span> SNOWFALL TOTALS
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 0.75rem; margin-bottom: 3px; color: #94a3b8;">
              <span>Past 24h:</span><span style="font-weight: 700; color: #f8fafc; font-family: monospace;">${formatSnow(s24h, units, 1)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 0.75rem; margin-bottom: 3px; color: #94a3b8;">
              <span>Past 72h:</span><span style="font-weight: 700; color: #f8fafc; font-family: monospace;">${formatSnow(s72h, units, 1)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 0.75rem; color: #94a3b8;">
              <span>Total Base:</span><span style="font-weight: 700; color: #38bdf8; font-family: monospace;">${formatSnow(sBase, units, 0)}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): void {}
}
